import { useEffect, useRef, useState } from "react"

interface UseCountUpOptions {
  end: number
  start?: number
  duration?: number
  decimals?: number
  easing?: (t: number) => number
  /**
   * While false, the hook is inert: no animation runs and the animate-once
   * latch is not armed. The count-up plays on the first render where
   * `enabled` is true — so a loading placeholder ("—") doesn't consume the
   * one animation the real value is entitled to.
   */
  enabled?: boolean
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

export function useCountUp({
  end,
  start = 0,
  duration = 1000,
  decimals = 0,
  easing = (t: number) => t * (2 - t), // ease-out quad
  enabled = true,
}: UseCountUpOptions) {
  // Evaluate skip conditions in the initializer (mirroring useTypewriter) so
  // reduced-motion users never see a one-frame flash of `start` before the
  // effect snaps to `end`.
  const [count, setCount] = useState(() => (prefersReducedMotion() || duration <= 0 ? end : start))
  const frameRef = useRef<number | null>(null)
  const startTimeRef = useRef<number | null>(null)
  const startValueRef = useRef(start)
  // Animate only once, on initial mount (DESIGN.md §6: "Only animate on
  // initial mount, not on re-renders"). Once settled, later `end` changes
  // (refetches, re-renders) snap straight to the new value instead of
  // replaying the count-up.
  const hasAnimatedRef = useRef(false)

  useEffect(() => {
    if (!enabled) return

    if (hasAnimatedRef.current) {
      setCount(end)
      return
    }

    // Respect prefers-reduced-motion: jump straight to the end value.
    if (prefersReducedMotion()) {
      hasAnimatedRef.current = true
      setCount(end)
      return
    }

    // If end value hasn't changed, don't animate
    if (end === count) {
      hasAnimatedRef.current = true
      return
    }

    // Cancel any ongoing animation
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current)
    }

    // Set starting point for animation
    startValueRef.current = count
    startTimeRef.current = Date.now()

    const animate = () => {
      const now = Date.now()
      const elapsed = now - (startTimeRef.current || now)
      const progress = Math.min(elapsed / duration, 1)

      // Apply easing function
      const easedProgress = easing(progress)

      // Calculate current value
      const currentValue = startValueRef.current + (end - startValueRef.current) * easedProgress

      // Round to specified decimals
      const roundedValue =
        Math.round(currentValue * Math.pow(10, decimals)) / Math.pow(10, decimals)
      setCount(roundedValue)

      // Continue animation if not complete
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate)
      } else {
        hasAnimatedRef.current = true
      }
    }

    frameRef.current = requestAnimationFrame(animate)

    // Cleanup function
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current)
      }
    }
  }, [end, duration, decimals, easing, enabled])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current)
      }
    }
  }, [])

  return count
}
